/**
 * Easter yard signs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterYardSignsPack: ToilePack = {
    slug: 'easter-yard-signs',
    category: 'season',
    name: 'Easter yard signs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'greeting', label: 'Greeting', subject: 'A wide yard sign lettered "HAPPY EASTER" large in bold with an egg border, no other text'},
        {key: 'arrow', label: 'Egg hunt arrow', subject: 'A wide yard sign lettered "EGG HUNT" with a big arrow and a trail of eggs'},
        {key: 'crossing', label: 'Bunny crossing', subject: 'A wide yard sign in the shape of a road sign lettered "BUNNY CROSSING" with a bunny silhouette'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel kawaii', style: 'pastel kawaii illustration, rounded shapes, soft outlines, friendly faces', palette: 'Soft pink, mint, lilac, butter yellow and cream', framing: 'Centered on a solid pastel background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: 'victorian easter card illustration, soft halftone grain, gentle colour', palette: 'Faded rose, sage green, cream and a little gold', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'modern', label: 'Modern minimal', style: 'modern minimal illustration, few flat shapes, no outlines', palette: 'Two or three flat spring colours on white', framing: 'Centered with generous white space around it'},
    ],
    directions: ['a chalkboard style instead', 'pointing the other way', 'with a wooden stake shown'],
    directionHint: 'try: a chalkboard style instead',
    seo: {
        title: 'Easter Yard Signs: Greeting, Egg Hunt & Crossing',
        metaDescription: 'Nine Easter yard signs — Happy Easter greeting, Egg Hunt arrow, Bunny Crossing — bold and road-readable, in pastel, vintage, and modern styles.',
        h1: 'Easter yard sign: happy-easter greeting, egg-hunt arrow, bunny crossing',
        tagline: 'Nine signs, three messages, three treatments each.',
        intro: 'Nine wide yard signs staked for the lawn, lettered bold enough to read from the road: a Happy Easter greeting with an egg border, an Egg Hunt directional with an arrow and a trail of eggs, and a road-sign-styled Bunny Crossing, each in a pastel kawaii, a Victorian vintage, and a modern minimal treatment.',
        useCases: [
            'a front-yard Easter decoration',
            'an egg-hunt event wayfinding sign',
            'a church or community Easter event',
            'a garden-center or shop lawn display',
        ],
        faq: [
            {q: 'How do I turn the design into an actual yard sign?', a: 'Print the design and mount it on corrugated plastic or foam board, or send the file to a print shop that offers yard-sign printing with a stake.'},
            {q: 'Is the lettering legible from a distance?', a: 'Yes — every sign is drawn bold and high-contrast specifically to read from the road, not just up close.'},
            {q: "What's the difference between the three styles?", a: 'Pastel kawaii uses rounded, friendly shapes in soft colour, vintage draws on Victorian Easter-card illustration, and modern minimal keeps to a few flat shapes and generous space.'},
            {q: 'Can I use the egg-hunt arrow to mark a real event route?', a: "Yes — it's built exactly for that: a clear directional arrow with a trail of eggs pointing the way."},
        ],
    },
}
